import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import ExpandableFilterPanel, {
  ExpandableFilterPanelItem,
} from 'components/molecules/expandable-filter-panel';
import useKey from 'infrastructure/hooks/utils/use-key';
import PatientSelectBox from 'components/molecules/patient-select-box';
import BooleanSelectBox from 'components/molecules/boolean-select-box';
import DoctorSelectBox from 'components/molecules/doctor-select-box';
import ClinicSelectBox from 'components/molecules/clinic-select-box';
import { RoleGroups } from 'infrastructure/consts/role-groups';
import BaseSelectBox from 'components/atoms/base-select-box';

import s from './styles.module.scss';

import type { INewPatientsReportListFilters } from 'infrastructure/interfaces';

interface IProps {
  data: INewPatientsReportListFilters;
  updateFilter: (data: INewPatientsReportListFilters) => void;
  resetFilter: () => void;
  count: number;
}

const dateRanges = [
  {
    range: 90,
    name: i18next.t('newPatients.last90Days'),
  },
  {
    range: 60,
    name: i18next.t('newPatients.last60Days'),
  },
  {
    range: 30,
    name: i18next.t('newPatients.Last30Days'),
  },
];

const ExpandableNewPatientsReportsFilters: React.FC<IProps> = (props) => {
  const { data, count, updateFilter, resetFilter } = props;

  const { t } = useTranslation();
  const { key, changeKey } = useKey();

  const handleReset = () => {
    if (resetFilter) resetFilter();
    setTimeout(changeKey, 200);
  };

  const dateRangeChange = (range: number) => {
    updateFilter({ range });
  };

  const dateRangesOptions = useMemo(() => {
    return dateRanges.map((item) => ({
      label: item.name,
      value: item.range,
      title: item.name,
    }));
  }, []);

  return (
    <ExpandableFilterPanel
      filtersCount={count}
      onReset={handleReset}
      openWithValue
      isExpanded
    >
      <div className={s.form} key={key}>
        <ExpandableFilterPanelItem borderBottom label={t('labels.dateRange')}>
          <BaseSelectBox
            label=""
            options={dateRangesOptions}
            value={dateRangesOptions.find((el) => el.value === data.range)}
            onChange={dateRangeChange}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem
          borderBottom
          label={t('labels.patientFilter')}
        >
          <PatientSelectBox
            clinicGuid={data.clinicGuid}
            doctorGuid={data.doctorGuid}
            placeholder={t('labels.searchPatient')}
            value={data.patientGuid}
            onChange={(patientGuid) => {
              updateFilter({ patientGuid, page: undefined });
            }}
            allowClear
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem borderBottom label={t('labels.sort')}>
          <BooleanSelectBox
            allowClear
            trueLabel={t('labels.oldest')}
            falseLabel={t('labels.newest')}
            value={data.reverse}
            onChange={(reverse) => {
              updateFilter({ reverse, page: undefined });
            }}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.provider')}>
          <DoctorSelectBox
            allowClear
            clinicGuid={data.clinicGuid}
            placeholder={t('labels.search')}
            roles={RoleGroups.RpmDashboardFilterDoctorSearchRoles}
            value={data.doctorGuid}
            onChange={(doctorGuid: any) => {
              updateFilter({
                doctorGuid,
                patientGuid: undefined,
                page: undefined,
              });
            }}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.clinic')}>
          <ClinicSelectBox
            allowClear
            placeholder={t('labels.search')}
            value={data.clinicGuid}
            onChange={(clinicGuid) => {
              updateFilter({
                clinicGuid,
                doctorGuid: undefined,
                patientGuid: undefined,
                page: undefined,
              });
            }}
          />
        </ExpandableFilterPanelItem>
      </div>
    </ExpandableFilterPanel>
  );
};

export default ExpandableNewPatientsReportsFilters;
